import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { CheckpointRequest, useCreateCheckpointMutation, useGetCheckpointQuery, useUpdateCheckpointMutation } from '../../../../../store/sync/sync'
import { useGetAccountQuery } from '../../../../../store/accountUserSettings/accountUserSettings'
import { Controller, useForm } from 'react-hook-form'
import { salesforceSettingsDefaultValues, SalesforceSettingsFormValues } from './models'
import React, { useEffect, useState } from 'react'
import { getAccountCRMIntegrationConfig } from '../../../../../models/Account'
import { Col, Container, Form, Row, Stack } from 'react-bootstrap'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { FormFeedback } from '../../../../../components/Form/FormFeedback'
import { Button } from '../../../../../components/Button/Button'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../common/integrations'

export interface ContactSyncSettingsProps {
  serviceId: string
}

export const ContactSyncSettings: React.FC<ContactSyncSettingsProps> = ({ serviceId }) => {
  const accountId = useAppSelector(selectAccountId)

  const checkpointRequest = {
    accountId,
    level: 'user',
    connector: serviceId,
    direction: 'inbound',
    object: 'contact_operation_hashed',
  } as CheckpointRequest

  const { data: checkpoint, isFetching: isGetCheckpointFetching } = useGetCheckpointQuery(checkpointRequest, { refetchOnMountOrArgChange: 10 })
  const { data: account, isFetching: isAccountFetching } = useGetAccountQuery({ accountId }, { refetchOnMountOrArgChange: 10 })
  const [createCheckpoint] = useCreateCheckpointMutation()
  const [updateCheckpoint] = useUpdateCheckpointMutation()

  const form = useForm<SalesforceSettingsFormValues>({ defaultValues: salesforceSettingsDefaultValues })
  const [isSyncDisabled, setIsSyncDisabled] = useState<boolean>(true)
  const [autoSync, setAutoSync] = useState<boolean | null>(false)
  const [isUpdating, setIsUpdating] = useState<boolean>(false)

  useEffect(() => {
    if (account && account.data) {
      const serviceIntegration = getAccountCRMIntegrationConfig(account.data, serviceId)
      if (!serviceIntegration) {
        setIsSyncDisabled(false)
        return
      }
      setIsSyncDisabled(serviceIntegration.isSyncDisabled ?? false)
    }
  }, [account, serviceId])

  useEffect(() => {
    setAutoSync(checkpoint?.data?.active ?? null)
  }, [checkpoint])

  useEffect(() => {
    form.setValue('inbound.contact.autoSync', autoSync || false)
  }, [autoSync, form.setValue, form])

  const handleCancel = () => {
    form.setValue('inbound.contact.autoSync', autoSync || false)
  }

  const onSubmit = async (data: SalesforceSettingsFormValues) => {
    try {
      setIsUpdating(true)
      if (autoSync == null) {
        if (data.inbound.contact.autoSync) {
          await createCheckpoint(checkpointRequest).unwrap()
        }
      } else {
        await updateCheckpoint({ ...checkpointRequest, active: data.inbound.contact.autoSync }).unwrap()
      }
    } catch (e) {
      console.error(e)
    } finally {
      setIsUpdating(false)
    }
  }

  if (!isUpdating && (isGetCheckpointFetching || isAccountFetching)) {
    return (
      <Container>
        <div className='skeleton mb-2' style={{ width: '100%', height: '70px' }} />
        <div className='skeleton mb-3' style={{ width: '100%', height: '70px' }} />
      </Container>
    )
  }

  return (
    <Container>
      <h6>Contacts</h6>
      <div>Keep your contacts in sync between Zocks and {getIntegrationDisplayName(serviceId as IntegrationServiceId)}.</div>
      <Row className='mb-1 mt-3'>
        <Col>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <div>Synchronize your contacts automatically</div>
          </Stack>
        </Col>
        <Col md='auto'>
          <Stack direction='horizontal' className='gap-4 pb-4 pb-md-0'>
            <Controller
              name='inbound.contact.autoSync'
              control={form.control}
              render={({ field, fieldState }) => (
                <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
                  <Form.Switch checked={field.value === true} onChange={field.onChange} disabled={isSyncDisabled || isAccountFetching} />
                  {fieldState.error && <FormFeedback>{fieldState.error.message}</FormFeedback>}
                </ScopedBlock>
              )}
            />
          </Stack>
        </Col>
      </Row>
      <Row className='mt-3'>
        <Col>
          <div className='d-flex gap-3 justify-content-end mb-4'>
            <Button variant='secondary' onClick={handleCancel} disabled={isUpdating}>
              Reset
            </Button>
            <ScopedBlock missingScopeAction={MissingScopeAction.DISABLE} scope={{ scope: 'integration:update', superScope: ['integration:admin'] }}>
              <Button variant='primary' onClick={form.handleSubmit(onSubmit)} loading={isUpdating}>
                Save
              </Button>
            </ScopedBlock>
          </div>
        </Col>
      </Row>
    </Container>
  )
}
